import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';

import { AdminModeService } from '../../../../auth/admin-mode/admin-mode.service';
import { UserService } from '../../../../auth/user/user.service';
import { SearchFormService } from '../../../customer-search-page/search-form.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { VerifyChoiceModalWrapper } from '../../../modal/verify-choice-modal/verify-choice-modal.wrapper';
import { AdminControlComponent } from './admin-control.component';

@Component({
  selector: 'cdf-admin-control-container',
  imports: [AdminControlComponent, VerifyChoiceModalWrapper],
  template: `
    <cdf-admin-control
      [(adminModeEnabled)]="adminModeToggleLocal"
      [(dpNr)]="selectedDpNrLocal"
      [regionalLocalEntities]="userService.legalEntitiesForAdminMode()"
      [showFeatureFlagNavigation]="showFeatureFlagNavigation()"
      (closeMenu)="closeMenu.emit()"
    />

    <cdf-verify-choice-modal-wrapper
      id="return-to-search-page-warning"
      [(open)]="displayReturnToSearchPageWarning"
      [headingText]="i18n('adminMode.backToSearch.heading')"
      [bodyText]="backToSearchPageMessage()"
      [verifyButtonText]="i18n('adminMode.backToSearch.verifyButtonLabel')"
      [cancelButtonText]="i18n('adminMode.backToSearch.cancelButtonLabel')"
      (verify)="returnToSearchPage()"
      (closeEvent)="resetLocalVariables()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminControlContainer implements AfterViewInit, OnDestroy {
  protected readonly i18n = useI18n();
  protected readonly userService = inject(UserService);
  private readonly adminModeService = inject(AdminModeService);
  private readonly searchFormService = inject(SearchFormService);
  protected readonly el = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly warnReturnToStart = input(false);
  readonly returnToStartPage = output<void>();
  readonly closeMenu = output<void>();

  // Signals for modal displays
  protected readonly displayReturnToSearchPageWarning = signal(false);

  // Local states for admin mode and dpNr
  protected readonly adminModeToggleLocal = signal(false);
  protected readonly selectedDpNrLocal = signal<string | undefined>(undefined);
  protected readonly isAdminModeActiveLocal = computed(() => this.adminModeToggleLocal() && !!this.selectedDpNrLocal());

  protected readonly backToSearchPageMessage = computed(() => {
    if (this.hasDpNrChanged() && this.adminModeService.selectedDpNr() && this.selectedDpNrLocal()) {
      return this.i18n('adminMode.backToSearch.message', {
        action: this.i18n('adminMode.backToSearch.changeDistributionPartner'),
      });
    }

    return this.i18n('adminMode.backToSearch.message', {
      action: this.adminModeToggleLocal()
        ? this.i18n('adminMode.backToSearch.activate')
        : this.i18n('adminMode.backToSearch.deactivate'),
    });
  });

  protected readonly showFeatureFlagNavigation = computed(() => this.adminModeService.hasAdminWritePermissions());

  // Observer for menu visibility
  private observer = new IntersectionObserver((entries) => {
    const menuIsVisible = entries[0].isIntersecting;

    if (!menuIsVisible) {
      this.onMenuClosed();
    }
  });

  ngAfterViewInit() {
    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy() {
    this.observer.disconnect();
  }

  /**
   * Called when the admin menu is closed.
   * Handles necessary checks and actions.
   */
  private onMenuClosed() {
    if (this.warnReturnToStart()) {
      this.handleAdminModeChangedOnProfilePage();
    } else {
      this.handleAdminModeChangedGeneral();
    }
  }

  /**
   * If either
   * 1. The admin-mode status (!!dpNr && adminToggle === true) changed or
   * 2. the used dpNr changed
   * display warning, that these changes can only be applied the user agrees to return to the search page.
   */
  private handleAdminModeChangedOnProfilePage() {
    if (this.hasAdminModeStatusChanged() || this.hasDpNrChanged()) {
      this.displayReturnToSearchPageWarning.set(true);
    }
  }

  /**
   * If either
   * 1. The admin-mode status (!!dpNr && adminToggle === true) changed or
   * 2. the used dpNr changed
   * clear search results.
   */
  private handleAdminModeChangedGeneral() {
    if (
      this.adminModeService.isAdminModeActive() !== this.isAdminModeActiveLocal() ||
      this.selectedDpNrLocal() !== this.adminModeService.selectedDpNr()
    ) {
      this.searchFormService.searchResults.set(undefined);
      this.saveLocalVariables();

      this.returnToStartPage.emit();
    }
  }

  private saveLocalVariables() {
    if (!this.selectedDpNrLocal()) {
      this.adminModeToggleLocal.set(false);
    }

    this.adminModeService.adminModeToggle.set(this.adminModeToggleLocal());
    this.adminModeService.selectedDpNr.set(this.selectedDpNrLocal());
  }

  /**
   * Resets local variables to the corresponding status of the admin-mode service.
   */
  protected resetLocalVariables() {
    this.selectedDpNrLocal.set(this.adminModeService.selectedDpNr());
    this.adminModeToggleLocal.set(this.adminModeService.adminModeToggle());
  }

  private hasAdminModeStatusChanged() {
    return this.adminModeService.isAdminModeActive() !== this.isAdminModeActiveLocal();
  }

  private hasDpNrChanged(): boolean {
    return this.adminModeService.selectedDpNr() !== this.selectedDpNrLocal();
  }

  protected returnToSearchPage() {
    this.searchFormService.searchResults.set(undefined);
    this.saveLocalVariables();
    this.displayReturnToSearchPageWarning.set(false);
    this.returnToStartPage.emit();
  }
}
